import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, { useState } from 'react';
import { parseTaskYaml, stringifyTaskYaml } from './taskYaml';
import { PageButton } from '../../../../atoms/PageButton';
import {
  Field,
  inputClassName,
  selectClassName,
} from '../../../../atoms/Surface';
import {
  blankObservation,
  nextColor,
  OBSERVATION_STATUSES,
  observationProblems,
  ObservationSpec,
  questionsOf,
  RegionSpec,
  TOOLS,
} from './taskDraft';

/** The modalities a region is most often limited to; none ticked means every modality. */
const MODALITIES = ['CR', 'DX', 'DR', 'MG', 'CT', 'MR', 'US', 'PT'];

const STATUS_TONES: Record<string, string> = {
  POSITIVE: 'bg-dl-tint text-dl-accent',
  UNCERTAIN: 'bg-dl-warning-tint text-dl-warning',
  NEGATIVE: 'bg-dl-sunken text-dl-muted',
};

function IconButton({
  label,
  onClick,
  disabled,
  tone = 'neutral',
  children,
}: React.PropsWithChildren<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'neutral' | 'danger';
}>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={classNames(
        'rounded-full p-1.5 transition disabled:cursor-default disabled:text-dl-faint disabled:hover:bg-transparent',
        tone === 'danger'
          ? 'text-dl-danger hover:bg-dl-danger-tint'
          : 'text-dl-accent hover:bg-dl-tint'
      )}
    >
      {children}
    </button>
  );
}

/** Colours as swatches: click one to change it, × to remove it (one stays), + to add one. */
function ColorsEditor({
  colors,
  onChange,
  name,
}: {
  colors: string[];
  onChange: (colors: string[]) => void;
  name: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-dl-muted">
        Colours{' '}
        <span className="font-normal text-dl-faint">
          (the first is the default)
        </span>
      </span>
      <ul
        className="flex flex-wrap items-center gap-2"
        aria-label={`Colours of ${name}`}
      >
        {colors.map((color, i) => (
          <li
            key={i}
            className="flex items-center gap-1 rounded-full bg-dl-surface py-1 pl-1 pr-2 ring-1 ring-dl-border"
          >
            <input
              type="color"
              aria-label={`Colour ${i + 1}`}
              value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : expand(color)}
              onChange={e =>
                onChange(colors.map((c, j) => (j === i ? e.target.value : c)))
              }
              className="h-7 w-7 cursor-pointer rounded-full border-0 bg-transparent p-0"
            />
            <span className="font-mono text-xs text-dl-muted">{color}</span>
            <button
              type="button"
              aria-label={`Remove colour ${color}`}
              disabled={colors.length === 1}
              onClick={() => onChange(colors.filter((_, j) => j !== i))}
              className="rounded-full p-0.5 text-dl-faint hover:bg-dl-danger-tint hover:text-dl-danger disabled:invisible"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={() => onChange([...colors, nextColor(colors)])}
            className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm text-dl-accent ring-1 ring-dl-border hover:bg-dl-tint"
          >
            <PlusIcon className="h-3.5 w-3.5" /> Colour
          </button>
        </li>
      </ul>
    </div>
  );
}

/** `#abc` → `#aabbcc`, for the colour picker, which only takes six digits. */
function expand(color: string): string {
  const m = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/.exec(color);
  return m ? `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}` : '#000000';
}

/** A region's questions (its characterisation) as YAML, edited in place; applied once it reads. */
function QuestionsEditor({
  region,
  onChange,
}: {
  region: RegionSpec;
  onChange: (characterisation: RegionSpec['characterisation']) => void;
}) {
  const [text, setText] = useState(() =>
    region.characterisation ? stringifyTaskYaml(region.characterisation) : ''
  );
  const [error, setError] = useState('');
  return (
    <Field label="Questions (characterisation: a JSON schema, as YAML)">
      <textarea
        className={classNames(inputClassName, 'h-auto py-2 font-mono text-xs', {
          'ring-dl-danger': !!error,
        })}
        rows={8}
        spellCheck={false}
        value={text}
        placeholder={
          'json:\n  properties:\n    visibility:\n      type: string\n      enum: [Visible, Peu visible]'
        }
        onChange={e => {
          setText(e.target.value);
          try {
            const value = e.target.value.trim()
              ? parseTaskYaml(e.target.value)
              : null;
            setError('');
            onChange(value);
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      />
      {error && <span className="text-sm text-dl-danger">{error}</span>}
    </Field>
  );
}

function RegionEditor({
  region,
  index,
  onChange,
  onRemove,
  removable,
}: {
  region: RegionSpec;
  index: number;
  onChange: (region: RegionSpec) => void;
  onRemove: () => void;
  removable: boolean;
}) {
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const questions = questionsOf(region);
  const modalities = region.selector?.modalities ?? [];
  const toolName = region.tool?.name ?? '';
  const tools =
    TOOLS.some(t => t.value === toolName) || !toolName
      ? TOOLS
      : [...TOOLS, { value: toolName, label: toolName }];
  const setSelector = (next: string[]) => {
    const selector = { ...(region.selector ?? {}), modalities: next };
    if (next.length === 0) delete selector.modalities;
    onChange({
      ...region,
      selector: Object.keys(selector).length ? selector : undefined,
    });
  };
  return (
    <div className="rounded-2xl bg-dl-sunken p-3">
      <div className="flex flex-wrap items-end gap-3">
        <span className="pb-2 text-sm font-medium text-dl-muted">
          Region {index + 1}
        </span>
        <Field label="Drawn as">
          <select
            className={selectClassName}
            value={toolName}
            onChange={e =>
              onChange({
                ...region,
                tool: { ...region.tool, name: e.target.value },
              })
            }
          >
            {tools.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tool shows the answer to">
          <select
            className={selectClassName}
            value={region.tool?.control ?? ''}
            disabled={questions.length === 0 && !region.tool?.control}
            onChange={e => {
              const tool = {
                ...region.tool,
                control: e.target.value || undefined,
              };
              if (!tool.control) delete tool.control;
              onChange({ ...region, tool });
            }}
          >
            <option value="">Nothing</option>
            {[
              ...new Set([
                ...questions,
                ...(region.tool?.control ? [region.tool.control] : []),
              ]),
            ].map(q => (
              <option key={q} value={q}>
                {q}
              </option>
            ))}
          </select>
        </Field>
        <div className="ml-auto pb-1">
          <IconButton
            label={`Remove region ${index + 1}`}
            tone="danger"
            disabled={!removable}
            onClick={onRemove}
          >
            <TrashIcon className="h-4 w-4" />
          </IconButton>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-sm font-medium text-dl-muted">Only on</span>
        {MODALITIES.map(m => {
          const on = modalities.includes(m);
          return (
            <button
              key={m}
              type="button"
              aria-pressed={on}
              onClick={() =>
                setSelector(
                  on ? modalities.filter(x => x !== m) : [...modalities, m]
                )
              }
              className={classNames(
                'rounded-full px-2.5 py-0.5 text-sm ring-1 transition',
                on
                  ? 'bg-dl-primary text-white ring-dl-primary'
                  : 'bg-dl-surface text-dl-muted ring-dl-border hover:ring-dl-primary'
              )}
            >
              {m}
            </button>
          );
        })}
        {modalities
          .filter(m => !MODALITIES.includes(m))
          .map(m => (
            <span
              key={m}
              className="rounded-full bg-dl-primary px-2.5 py-0.5 text-sm text-white"
            >
              {m}
            </span>
          ))}
        <span className="text-sm text-dl-faint">
          {modalities.length === 0 ? 'every modality' : ''}
        </span>
      </div>
      <div className="mt-3">
        <button
          type="button"
          aria-expanded={questionsOpen}
          onClick={() => setQuestionsOpen(open => !open)}
          className="flex items-center gap-1 text-sm text-dl-accent hover:underline"
        >
          <ChevronDownIcon
            className={classNames('h-4 w-4 transition', {
              '-rotate-90': !questionsOpen,
            })}
          />
          {questions.length
            ? `${questions.length} question${questions.length > 1 ? 's' : ''}: ${questions.join(', ')}`
            : 'No question asked'}
        </button>
        {questionsOpen && (
          <div className="mt-2">
            <QuestionsEditor
              region={region}
              onChange={characterisation => {
                const next = { ...region, characterisation };
                if (!characterisation) delete next.characterisation;
                onChange(next);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ObservationCard({
  observation,
  index,
  count,
  problems,
  open,
  onToggle,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
}: {
  observation: ObservationSpec;
  index: number;
  count: number;
  problems: string[];
  open: boolean;
  onToggle: () => void;
  onChange: (o: ObservationSpec) => void;
  onMove: (by: number) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const name =
    observation.label || observation.code || `observation ${index + 1}`;
  const colors = Array.isArray(observation.colors) ? observation.colors : [];
  const regions = Array.isArray(observation.regions) ? observation.regions : [];
  return (
    <li
      aria-label={`Observation ${index + 1}`}
      className={classNames(
        'rounded-2xl bg-dl-surface ring-1',
        problems.length ? 'ring-dl-danger/50' : 'ring-dl-border'
      )}
    >
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? 'Fold' : 'Edit'} ${name}`}
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <ChevronDownIcon
            className={classNames('h-4 w-4 shrink-0 text-dl-accent transition', {
              '-rotate-90': !open,
            })}
          />
          <span className="flex shrink-0 -space-x-1">
            {colors.map((c, i) => (
              <span
                key={i}
                className="h-4 w-4 rounded-full ring-2 ring-white"
                style={{ background: c }}
              />
            ))}
          </span>
          <span className="truncate font-mono text-sm">
            {observation.code || '—'}
          </span>
          <span
            className={classNames(
              'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
              STATUS_TONES[observation.status] ?? 'bg-dl-sunken'
            )}
          >
            {observation.status}
          </span>
          <span className="truncate text-dl-text">{observation.label}</span>
          <span className="ml-auto shrink-0 text-sm text-dl-faint">
            {regions
              .map(
                r =>
                  TOOLS.find(t => t.value === r.tool?.name)?.label ??
                  r.tool?.name ??
                  '?'
              )
              .join(', ')}
          </span>
          {problems.length > 0 && (
            <span className="shrink-0 rounded-full bg-dl-danger-tint px-2 py-0.5 text-xs text-dl-danger">
              {problems.length} to fix
            </span>
          )}
        </button>
        <span className="flex shrink-0 items-center">
          <IconButton
            label={`Move ${name} up`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUpIcon className="h-4 w-4" />
          </IconButton>
          <IconButton
            label={`Move ${name} down`}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDownIcon className="h-4 w-4" />
          </IconButton>
          <IconButton label={`Duplicate ${name}`} onClick={onDuplicate}>
            <DocumentDuplicateIcon className="h-4 w-4" />
          </IconButton>
          <IconButton label={`Delete ${name}`} tone="danger" onClick={onRemove}>
            <TrashIcon className="h-4 w-4" />
          </IconButton>
        </span>
      </div>
      {open && (
        <div className="flex flex-col gap-4 border-t border-dl-border px-4 py-4">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Code">
              <input
                className={classNames(inputClassName, 'w-44 font-mono text-sm')}
                value={observation.code ?? ''}
                placeholder="NODULE"
                onChange={e =>
                  onChange({
                    ...observation,
                    code: e.target.value.toUpperCase().replace(/\s+/g, '_'),
                  })
                }
              />
            </Field>
            <Field label="Status">
              <select
                className={selectClassName}
                value={observation.status}
                onChange={e =>
                  onChange({ ...observation, status: e.target.value })
                }
              >
                {OBSERVATION_STATUSES.map(s => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Label" className="min-w-[12rem] flex-1">
              <input
                className={inputClassName}
                value={observation.label ?? ''}
                placeholder="Nodule"
                onChange={e =>
                  onChange({ ...observation, label: e.target.value })
                }
              />
            </Field>
          </div>
          <ColorsEditor
            colors={colors}
            name={name}
            onChange={next => onChange({ ...observation, colors: next })}
          />
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-dl-muted">
              How it is drawn{' '}
              <span className="font-normal text-dl-faint">
                (one region per tool or modality)
              </span>
            </span>
            {regions.map((region, j) => (
              <RegionEditor
                key={j}
                region={region}
                index={j}
                removable={regions.length > 1}
                onChange={next =>
                  onChange({
                    ...observation,
                    regions: regions.map((r, k) => (k === j ? next : r)),
                  })
                }
                onRemove={() =>
                  onChange({
                    ...observation,
                    regions: regions.filter((_, k) => k !== j),
                  })
                }
              />
            ))}
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...observation,
                  regions: [...regions, { tool: { name: TOOLS[0].value } }],
                })
              }
              className="flex items-center gap-1 self-start rounded-full px-3 py-1 text-sm text-dl-accent ring-1 ring-dl-border hover:bg-dl-tint"
            >
              <PlusIcon className="h-3.5 w-3.5" /> Region
            </button>
          </div>
          {problems.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-dl-danger">
              {problems.map(p => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The task's observations as cards: each edited in place (code, status, label, colours, the regions it is
 * drawn with and the questions asked), moved, duplicated or deleted — a deletion can be undone.
 */
export function ObservationsEditor({
  observations,
  onChange,
}: {
  observations: ObservationSpec[];
  onChange: (observations: ObservationSpec[]) => void;
}) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [deleted, setDeleted] = useState<{
    observation: ObservationSpec;
    index: number;
  } | null>(null);
  const problems = observationProblems(observations);
  const usedColors = observations.flatMap(o =>
    Array.isArray(o.colors) ? o.colors : []
  );

  const toggle = (i: number) =>
    setOpen(current => {
      const next = new Set(current);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  /** Open cards follow their observation when the list shifts. */
  const shiftOpen = (map: (i: number) => number | null) =>
    setOpen(
      current =>
        new Set([...current].map(map).filter((i): i is number => i !== null))
    );

  return (
    <div className="flex flex-col gap-2">
      {deleted && (
        <div
          role="status"
          className="flex items-center gap-3 rounded-2xl bg-dl-sunken px-4 py-2 text-sm"
        >
          <span className="text-dl-text">
            Deleted{' '}
            {deleted.observation.label ||
              deleted.observation.code ||
              'an observation'}
            .
          </span>
          <button
            type="button"
            className="font-medium text-dl-accent hover:underline"
            onClick={() => {
              const next = [...observations];
              next.splice(deleted.index, 0, deleted.observation);
              shiftOpen(i => (i >= deleted.index ? i + 1 : i));
              onChange(next);
              setDeleted(null);
            }}
          >
            Undo
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            className="ml-auto text-dl-faint"
            onClick={() => setDeleted(null)}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      <ul className="flex flex-col gap-2">
        {observations.map((observation, i) => (
          <ObservationCard
            key={i}
            observation={observation}
            index={i}
            count={observations.length}
            problems={problems[i]}
            open={open.has(i)}
            onToggle={() => toggle(i)}
            onChange={next =>
              onChange(observations.map((o, j) => (j === i ? next : o)))
            }
            onMove={by => {
              const next = [...observations];
              const [moved] = next.splice(i, 1);
              next.splice(i + by, 0, moved);
              shiftOpen(j => (j === i ? i + by : j === i + by ? i : j));
              onChange(next);
            }}
            onDuplicate={() => {
              const copy = structuredClone(observation);
              const next = [...observations];
              next.splice(i + 1, 0, {
                ...copy,
                code: `${observation.code}_COPY`,
              });
              shiftOpen(j => (j > i ? j + 1 : j));
              setOpen(current => new Set([...current, i + 1]));
              onChange(next);
            }}
            onRemove={() => {
              setDeleted({ observation, index: i });
              shiftOpen(j => (j === i ? null : j > i ? j - 1 : j));
              onChange(observations.filter((_, j) => j !== i));
            }}
          />
        ))}
      </ul>
      <PageButton
        className="mx-0 flex items-center gap-1 self-start px-4"
        onClick={() => {
          setOpen(current => new Set([...current, observations.length]));
          onChange([...observations, blankObservation(usedColors)]);
        }}
      >
        <PlusIcon className="h-4 w-4" /> Add observation
      </PageButton>
    </div>
  );
}
